import { forwardRef } from 'react';

import classNames from 'classnames';

import styles from './FormField.module.css';
import { PersonModalTickSVG } from '../../svgComponents/personModalTickSVG/PersonModalTickSVG';
import { FormFieldError } from '../FormFieldError/FormFieldError';

type PartiallyRequired<T, K extends keyof T> = Omit<T, K> &
    Required<Pick<T, K>>;
type TInputFieldProps<
    GType extends string,
    GInputType = React.InputHTMLAttributes<HTMLInputElement>
> = {
    type: GType;
    label?: string;
    input?: GInputType;
    hideError?: boolean;
    'data-testid'?: string;
    meta?: {
        pristine?: boolean;
        touched?: boolean;
        error?: string;
    };
};

// Ensures compatibility with typescript and react
// It's not terministic if "true" or true is being passed
// Hence this stupid helper function
function isCheckboxActive(
    value: string | boolean | number | readonly string[] | undefined
): boolean {
    return value === 'true' || value === true;
}

type TCheckboxInputProps = React.InputHTMLAttributes<HTMLInputElement> &
    PartiallyRequired<TInputFieldProps<'checkbox'>, 'label'>;
const CHECKBOX_ACTIVE_CLASSNAME = 'is-active';
const CheckboxInput = forwardRef<HTMLInputElement, TCheckboxInputProps>(
    (props, ref) => {
        const {
            // TODO: Remove the `input` prop after migrating to the hook-form
            input = undefined,
            label,
            disabled,
            type,
            placeholder,
            hideError,
            meta: { pristine, touched, error } = {},
            // Input element is not allowed to have children, hence we need to remove it from the props
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            children: _children,
            // Allows forward compatibility to get rid of the `input` prop
            ...rest
        } = props;

        const value = input !== undefined ? input.value : rest.value;
        const name = input !== undefined ? input.name : rest.name;

        const isActive = isCheckboxActive(value)
            ? CHECKBOX_ACTIVE_CLASSNAME
            : null;

        const checkboxStyles = classNames(styles.checkboxBackground, {
            [styles[CHECKBOX_ACTIVE_CLASSNAME]]: isActive
        });

        return (
            <div className={classNames(styles.checkbox, 'relative')}>
                {(!pristine || touched) && (
                    <FormFieldError hide={hideError} message={error} />
                )}

                <label
                    htmlFor={`checkboxInput-${name}`}
                    className={styles.label}
                >
                    <div className={checkboxStyles}>
                        <div className={styles.tick}>
                            <PersonModalTickSVG />
                        </div>
                    </div>
                    <input
                        {...rest}
                        {...input}
                        value={value}
                        name={name}
                        checked={isActive !== null}
                        id={`checkboxInput-${name}`}
                        disabled={disabled}
                        type={type}
                        placeholder={placeholder}
                        className={styles.input}
                        data-testid={props['data-testid']}
                        ref={ref}
                    />
                    <span className={styles.labelText}>{label}</span>
                </label>
            </div>
        );
    }
);
CheckboxInput.displayName = 'CheckboxInput';

type TTextInputProps = React.InputHTMLAttributes<HTMLInputElement> &
    TInputFieldProps<'text'>;
export const TextInput = forwardRef<
    HTMLInputElement,
    Omit<TTextInputProps, 'type'>
>((props, ref) => {
    const {
        // TODO: Remove the `input` prop after migrating to the hook-form
        input = undefined,
        placeholder,
        disabled,
        readOnly,
        label,
        hideError,
        meta: { touched, error } = {},
        // Input element is not allowed to have children, hence we need to remove it from the props
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        children: _children,
        // Allows forward compatibility to get rid of the `input` prop
        ...rest
    } = props;

    return (
        <div className="relative">
            {label && (
                <label htmlFor={props.id} className={styles.label}>
                    {label}
                </label>
            )}
            {touched && <FormFieldError hide={hideError} message={error} />}
            {/* eslint-disable-next-line iwoca/input-a11y */}
            <input
                {...rest}
                {...input}
                id={props.id}
                className={styles.input}
                placeholder={placeholder}
                data-testid={props['data-testid']}
                disabled={disabled}
                readOnly={!!readOnly}
                ref={ref}
            />
        </div>
    );
});
TextInput.displayName = 'TextInput';

export const renderField = (props: TCheckboxInputProps | TTextInputProps) => {
    switch (props.type) {
        case 'checkbox':
            return <CheckboxInput {...props} />;
        default:
            return <TextInput {...props} />;
    }
};
